import { parseServerDate } from "src/utils/dateTime";
import { browserLocale } from "../../../locales/display.ts";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { SpinnerProgress } from "@ui/spinner/SpinnerProgress";
import { Typo } from "@ui/text/Typo";
import { useState } from "react";
import { GD } from "src/GD";
import { useMobile } from "src/hooks/useMobile";
import { IPagination, Pagination } from "../Pagination";
import { Badge } from "@ui/badge/Badge";
import { DatePickerField, DateRangePicker, toUTCISODate } from "../DateRangePicker";
import Decimal from "decimal.js";
import { IconPayments } from "src/assets/icon_payments";
import { IconMonitoring } from "src/assets/icon_monitoring";
import { IconBank } from "src/assets/icon_bank";
import { DectaSessionsTable } from "./DectaSessionsTable";
import { NoRecords } from "../../providers/NoRecords";

interface ISessionStat {
	financial: {
		settled: { count: number; amount: number };
		pending: { count: number; amount: number };
		failed: { count: number; amount: number };
		settlement_failed: number;
	};
	technical: {
		abandoned: number;
		active_processing: number;
	};
	antifraud: {
		passed: number;
		rejected: number;
		errors: number;
	};
}

const StatValue = ({ label, value, color, sub }: { label: string; value: string | number; color: string; sub?: string }) => { useLocale(); return ((
	<VBox gap="4px" sx={{ alignItems: "flex-start", minWidth: "70px" }}>
		<Typo variant="caption" sx={{ color: "#64748B", fontSize: "11px", whiteSpace: "nowrap" }}>
			{label}
		</Typo>
		<Typo variant="medium" sx={{ color, fontWeight: 700, fontSize: "18px", lineHeight: "1.2" }}>
			{value}
		</Typo>
		{sub && (
			<Typo variant="caption" sx={{ color, fontSize: "14px", fontWeight: 600, opacity: 0.9, whiteSpace: "nowrap" }}>
				{sub} EUR
			</Typo>
		)}
	</VBox>
)); };

const StatGroup = ({ title, icon, children, flex }: { title: string; icon: React.ReactNode; children: React.ReactNode; flex?: string }) => { useLocale(); return ((
	<Box
		sx={{
			flex: flex ?? "1",
			minWidth: "200px",
			backgroundColor: "#FFFFFF",
			borderRadius: "10px",
			padding: "12px 16px",
			border: "1px solid #E2E8F0",
		}}>
		<HBox gap="6px" sx={{ alignItems: "center", marginBottom: "10px" }}>
			<Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
			<Typo variant="caption" sx={{ fontWeight: 700, color: "#334155", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
				{title}
			</Typo>
		</HBox>
		<HBox gap="0px" sx={{ justifyContent: "space-between", "& > *": { flex: 1 } }}>
			{children}
		</HBox>
	</Box>
)); };

const SessionStats = ({ stat }: { stat: ISessionStat }) => {
	useLocale();
	const fmtAmount = (cents: number) => new Decimal(cents).dividedBy(100).toFixed(2);

	return (
		<HBox gap="10px" sx={{ flexWrap: "wrap" }}>
			<StatGroup flex="0.45" title={msg("turnover_8ecaf1d7")} icon={<IconBank width={22} height={22} color="#94A3B8" />}>
				<StatValue label={msg("settled")} value={fmtAmount(stat.financial.settled.amount) + " EUR"} color="#16A34A" />
				<StatValue label={msg("pending")} value={fmtAmount(stat.financial.pending.amount) + " EUR"} color="#64748B" />
				<StatValue label={msg("failed_031a8f0f")} value={fmtAmount(stat.financial.failed?.amount ?? 0) + " EUR"} color="#DC2626" />
			</StatGroup>
			<StatGroup flex="0.30" title={msg("financial")} icon={<IconPayments width={22} height={22} color="#94A3B8" />}>
				<StatValue label={msg("settled")} value={stat.financial.settled.count} color="#16A34A" />
				<StatValue label={msg("pending")} value={stat.financial.pending.count} color="#64748B" />
				<StatValue label={msg("failed_031a8f0f")} value={stat.financial.failed?.count ?? stat.financial.settlement_failed} color="#DC2626" />
			</StatGroup>
			<StatGroup flex="0.25" title={msg("technical")} icon={<IconMonitoring width={22} height={22} color="#94A3B8" />}>
				<StatValue label={msg("abandoned")} value={stat.technical.abandoned} color="#64748B" />
				<StatValue label={msg("processing")} value={stat.technical.active_processing} color="#64748B" />
			</StatGroup>
		</HBox>
	);
};

const DEFAULT_FROM_DATE_DAYS = 30;
const END_OF_DAY_HOUR = 23;
const END_OF_DAY_MINUTE = 59;
const END_OF_DAY_SECOND = 59;
const END_OF_DAY_MILLISECOND = 999;

const defaultFromDate = () => {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - DEFAULT_FROM_DATE_DAYS);
	d.setUTCHours(0, 0, 0, 0);
	return d;
};

const defaultToDate = () => {
	const d = new Date();
	d.setUTCHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND);
	return d;
};

const getValidDateOrFallback = (value: Date | string, fallback: () => Date): Date => {
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? fallback() : date;
};

const formatDateParam = (date: Date): string => {
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

// const FailReasonCell = ({ reason }: { reason: string }) => {
// 	const entries = parseFailReason(reason);

// 	if (entries.length === 0) {
// 		return (
// 			<VBox>
// 				<Typo variant="caption" color="textSecondary">
// 					Fail reason:
// 				</Typo>
// 				<Typo variant="medium" sx={{ color: "#DC2626" }}>
// 					{cleanRaw(reason)}
// 				</Typo>
// 			</VBox>
// 		);
// 	}

// 	return (
// 		<HoverTooltip
// 			align="end"
// 			style={{ position: "relative", cursor: "pointer" }}
// 			tooltipStyle={{ minWidth: "240px", maxWidth: "380px" }}
// 			content={
// 				<table style={{ borderCollapse: "collapse", width: "100%" }}>
// 					<tbody>
// 						{entries.map(([key, value], i) => (
// 							<tr key={i}>
// 								<td
// 									style={{
// 										color: "#9CA3AF",
// 										fontSize: "12px",
// 										fontWeight: 500,
// 										whiteSpace: "nowrap",
// 										paddingRight: "12px",
// 										paddingTop: "3px",
// 										paddingBottom: "3px",
// 										verticalAlign: "top",
// 									}}>
// 									{key}
// 								</td>
// 								<td
// 									style={{
// 										color: "#FFFFFF",
// 										fontSize: "12px",
// 										fontWeight: 500,
// 										wordBreak: "break-word",
// 										paddingTop: "3px",
// 										paddingBottom: "3px",
// 										verticalAlign: "top",
// 									}}>
// 									{value}
// 								</td>
// 							</tr>
// 						))}
// 					</tbody>
// 				</table>
// 			}>
// 			<Typo
// 				variant="medium"
// 				sx={{
// 					color: "#DC2626",
// 					textDecoration: "underline dotted",
// 					textUnderlineOffset: "3px",
// 				}}>
// 				Fail details
// 			</Typo>
// 		</HoverTooltip>
// 	);
// };

const perPage = 20;

export interface IDectaSessionsProps {}

interface IFilterOption {
	name: string;
	value: string;
}

const antifraudStatusOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("started"); }, value: "started" },
	{ get name() { return msg("success"); }, value: "success" },
	{ get name() { return msg("failed_031a8f0f"); }, value: "failed" },
	{ get name() { return msg("error"); }, value: "error" },
];

const authorizationStatusOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("init"); }, value: "init" },
	{ name: "3DS", value: "3ds" },
	{ get name() { return msg("pay"); }, value: "pay" },
	{ get name() { return msg("failed_031a8f0f"); }, value: "failed" },
	{ get name() { return msg("pending"); }, value: "pending" },
	{ get name() { return msg("completed"); }, value: "completed" },
	{ get name() { return msg("expired"); }, value: "expired" },
];

const settlementStatusOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("completed"); }, value: "completed" },
	{ get name() { return msg("unknown"); }, value: "unknown" },
	{ get name() { return msg("pending"); }, value: "pending" },
	{ get name() { return msg("failed_031a8f0f"); }, value: "failed" },
];

const typeOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ name: "DMS", value: "dms" },
	{ name: "SMS", value: "sms" },
	{ get name() { return msg("return"); }, value: "return" },
	{ get name() { return msg("refund"); }, value: "refund" },
	{ get name() { return msg("payout"); }, value: "payout" },
	{ name: "AP", value: "ap" },
	{ name: "GP", value: "gp" },
];

export const DectaSessions = ({}: IDectaSessionsProps) => {
	useLocale();
	const { isMobile } = useMobile();
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);

	const [antifraudStatus, setAntifraudStatus] = useState<IFilterOption | undefined>(antifraudStatusOptions[0]);
	const [authorizationStatus, setAuthorizationStatus] = useState<IFilterOption | undefined>(authorizationStatusOptions[0]);
	const [settlementStatusFilter, setSettlementStatusFilter] = useState<IFilterOption | undefined>(settlementStatusOptions[0]);
	const [typeFilter, setTypeFilter] = useState<IFilterOption | undefined>(typeOptions[0]);

	const [searchObject, setSearchObject] = useState<{ search: string }>({ search: "" });

	const [fromDate, setFromDate] = useState<Date>(defaultFromDate());
	const [toDate, setToDate] = useState<Date>(defaultToDate());
	const [settlementDate, setSettlementDate] = useState("");

	const getFilterParams = (search: string, extraParams?: Record<string, string | undefined>) => {
		const params: Record<string, string | undefined> = {};
		if (antifraudStatus?.value) params.antifraud_status = antifraudStatus.value;
		if (authorizationStatus?.value) params.status = authorizationStatus.value;
		if (settlementStatusFilter?.value) params.settlement_status = settlementStatusFilter.value;
		if (typeFilter?.value) params.type = typeFilter.value;
		if (settlementDate) params.settlement_date = settlementDate;
		if (search) {
			const searchToDate = new Date();
			const searchFromDate = new Date(searchToDate);
			searchFromDate.setFullYear(searchFromDate.getUTCFullYear() - 1);

			params.from_date = formatDateParam(searchFromDate);
			params.to_date = formatDateParam(searchToDate);
		} else {
			params.from_date = formatDateParam(fromDate);
			params.to_date = formatDateParam(toDate);
		}

		return { ...params, ...extraParams };
	};

	const getRequestData = (page: number, search: string, filterParams?: Record<string, string | undefined>) => ({
		method: "processing/sessions",
		data: { page: page, limit: perPage, filter: { search: search, ...getFilterParams(search, filterParams) } },
	});

	const [downloadBusy, setDownloadBusy] = useState(false);

	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequestData(currentPage, searchObject.search));

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequestData(page, searchObject.search));
	};

	const handleSearch = () => {
		setCurrentPage(0);
		reload(getRequestData(0, searchObject.search));

		setSearchObject({ search: searchObject.search });
	};

	const resetSearch = () => {
		const nextFromDate = defaultFromDate();
		const nextToDate = defaultToDate();

		setSearchObject({ search: "" });
		setAntifraudStatus(antifraudStatusOptions[0]);
		setAuthorizationStatus(authorizationStatusOptions[0]);
		setSettlementStatusFilter(settlementStatusOptions[0]);
		setTypeFilter(typeOptions[0]);
		setFromDate(nextFromDate);
		setToDate(nextToDate);
		setSettlementDate("");
		setCurrentPage(0);
		reload(
			getRequestData(0, "", {
				antifraud_status: undefined,
				status: undefined,
				settlement_status: undefined,
				type: undefined,
				settlement_date: undefined,
				from_date: formatDateParam(nextFromDate),
				to_date: formatDateParam(nextToDate),
			}),
		);
	};

	const handleRefresh = () => {
		reload(getRequestData(currentPage, searchObject.search));
	};

	const handleDownloadCsv = async () => {
		setDownloadBusy(true);
		try {
			const reqData = {
				method: "processing/sessions",
				data: {
					page: 0,
					limit: perPage,
					filter: { search: searchObject.search, ...getFilterParams(searchObject.search) },
					download: true,
				},
			};
			const res = await GD.REQ_API.request(reqData);
			const items = res?.data?.items as IDectaSession[] | undefined;
			if (!items || items.length === 0) return;

			const headers = [
				"id",
				"created_at",
				"country",
				"card_network",
				"decta_payment_id",
				"card_mask",
				"email",
				"merchant_id",
				"type",
				"amount",
				"currency",
				"fail_reason",
				"settlement_status",
				"status",
				"settlement_date",
			];
			const escapeCell = (val: unknown) => {
				const str = val == null ? "" : String(val);
				return str.includes(",") || str.includes('"') || str.includes("\n") ? '"' + str.replace(/"/g, '""') + '"' : str;
			};
			const formatCsvDate = (val: unknown) => {
				if (!val) return "";
				const date = new Date(String(val));
				return Number.isNaN(date.getTime()) ? String(val) : date.toLocaleString();
			};
			const rows = items.map(item =>
				headers
					.map(h => {
						const val = (item as unknown as Record<string, unknown>)[h];
						if (h === "amount" && typeof val === "number") return new Decimal(val).dividedBy(100).toFixed(2);
						if (h === "settlement_date") return escapeCell(formatCsvDate(val));
						return escapeCell(val);
					})
					.join(","),
			);

			const csv = [headers.join(","), ...rows].join("\n");
			const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;

			const nameParts: string[] = ["decta_sessions"];
			if (searchObject.search) nameParts.push(searchObject.search.replace(/[^a-zA-Z0-9]/g, "_"));
			if (typeFilter?.value) nameParts.push(typeFilter.value);
			if (antifraudStatus?.value) nameParts.push("af_" + antifraudStatus.value);
			if (authorizationStatus?.value) nameParts.push("auth_" + authorizationStatus.value);
			if (settlementStatusFilter?.value) nameParts.push("settle_" + settlementStatusFilter.value);
			if (settlementDate) nameParts.push("settlement_date_" + settlementDate);
			nameParts.push(formatDateParam(fromDate));
			nameParts.push(formatDateParam(toDate));
			a.download = nameParts.join("_") + ".csv";

			a.click();
			URL.revokeObjectURL(url);
		} finally {
			setDownloadBusy(false);
		}
	};

	const paginationData: IPagination | undefined = response?.data?.pagination;

	const isRawData = response?.data?.raw === true;
	const responseItems: IDectaSession[] = Array.isArray(response?.data)
		? response.data
		: Array.isArray(response?.data?.items)
			? response.data.items
			: [];
	const hasResponse = response !== null && response !== undefined;
	const initialLoading = busy && !hasResponse;
	const noData = hasResponse && !busy && !response?.error && responseItems.length === 0;

	const getAntifraudStatusByValue = (value: string): IFilterOption | undefined => {
		const option = antifraudStatusOptions.find(opt => opt.value === value);
		return option;
	};

	const getAuthorizationStatusByValue = (value: string): IFilterOption | undefined => {
		const option = authorizationStatusOptions.find(opt => opt.value === value);
		return option;
	};

	const getSettlementStatusByValue = (value: string): IFilterOption | undefined => {
		const option = settlementStatusOptions.find(opt => opt.value === value);
		return option;
	};

	const getTypeByValue = (value: string): IFilterOption | undefined => {
		const option = typeOptions.find(opt => opt.value === value);
		return option;
	};

	return (
		<VBox sx={{ flex: 1, minWidth: 0 }}>
			<HBox gap="8px" sx={{ flexWrap: "wrap", alignItems:'start' }}>
				<Input sx={{ minWidth: "190px", flex:1 }} size="small" type="search" name="search" placeholder={msg("searching")} data={searchObject} label={msg("search")} />

				<Box sx={{ width: "300px", maxWidth: "100%" }}>
					<DateRangePicker timeZone="UTC"
						label={msg("transactionDate")}
						from={toUTCISODate(fromDate)}
						to={toUTCISODate(toDate)}
						onChange={(from, to) => {
							setFromDate(getValidDateOrFallback(parseServerDate(from) ?? "", defaultFromDate));
							setToDate(getValidDateOrFallback(parseServerDate(to) ?? "", defaultToDate));
						}}
					/>
				</Box>

				<Select<IFilterOption>
					// flex="1"
					sx={{ minWidth: "160px", maxWidth: "220px" }}
					size="small"
					label={msg("antifraudStatus")}
					value={antifraudStatus}
					values={antifraudStatusOptions}
					onChange={v => {
						setAntifraudStatus(getAntifraudStatusByValue(v as unknown as string));
					}}
				/>
				<Select<IFilterOption>
					// flex="1"
					sx={{ minWidth: "160px", maxWidth: "220px" }}
					size="small"
					label={msg("authorizationStatus")}
					value={authorizationStatus}
					values={authorizationStatusOptions}
					onChange={v => setAuthorizationStatus(getAuthorizationStatusByValue(v as unknown as string))}
				/>
				<Select<IFilterOption>
					// flex="1"
					sx={{ minWidth: "160px", maxWidth: "220px" }}
					size="small"
					label={msg("settlementStatus")}
					value={settlementStatusFilter}
					values={settlementStatusOptions}
					onChange={v => setSettlementStatusFilter(getSettlementStatusByValue(v as unknown as string))}
				/>
				<DatePickerField timeZone="UTC" style={{ width: "190px" }} label={msg("settlementDate")} value={settlementDate} allowClear placeholder={msg("anyDate")} onChange={setSettlementDate} />
				<Select<IFilterOption>
					// flex="1"
					sx={{ minWidth: "160px", maxWidth: "220px" }}
					size="small"
					label={msg("type")}
					value={typeFilter}
					values={typeOptions}
					onChange={v => setTypeFilter(getTypeByValue(v as unknown as string))}
				/>

				

				<HBox gap="8px" sx={{ alignSelf: "end", height: "36px", marginLeft: "auto" }}>
					<Button disabled={busy} onClick={() => resetSearch()} variant="secondarySmall" title={msg("reset")} />
					<Button disabled={busy} onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
				</HBox>
			</HBox>

			<Spacer size="10px" />
			<ProgressBar busy={busy} />

			{response?.data?.stat && <SessionStats stat={response.data.stat as ISessionStat} />}

			{initialLoading ? (
				<SpinnerProgress busy />
			) : (
				<Box sx={{ opacity: busy ? 0.45 : 1, transition: "opacity 0.2s ease", pointerEvents: busy ? "none" : "auto" }}>
					{isRawData ? (
						<>
							<Spacer size="10px" />
							{responseItems.length > 0 &&
						(() => {
							const items = responseItems as unknown as Record<string, any>[];
							const keys = Object.keys(items[0]);
							const cols = keys.map(() => "minmax(140px, max-content)").join(" ");
							return (
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: cols,
										padding: "0px",
										backgroundColor: "#FFFFFF",
										borderRadius: "8px",
										overflow: "hidden",
										width: "fit-content",
									}}>
									{keys.map(key => (
										<Box key={"h_" + key} sx={{ padding: "10px 16px", backgroundColor: "#c6d2df", borderBottom: "1px solid #E2E8F0" }}>
											<Typo
												variant="caption"
												sx={{ color: "#131d2b", fontWeight: 600, whiteSpace: "nowrap", textTransform: "capitalize" }}>
												{key.replace(/_/g, " ")}
											</Typo>
										</Box>
									))}
									{/* rows */}
									{items.map((item, index) =>
										keys.map(key => {
											const value = item[key];
											let content: React.ReactNode = value != null ? String(value) : "";

											if (key === "status" && value) {
												content = (
													<Box sx={{ width: "100%" }}>
														<Badge key={key} label={value} />
													</Box>
												);
											} else if (key === "yyyymm" && value) {
												const str = String(value);
												if (str.length >= 6) {
													const year = parseInt(str.substring(0, 4), 10);
													const month = parseInt(str.substring(4, 6), 10) - 1;
													const date = new Date(year, month);
													content = (
														<Typo variant="medium">{date.toLocaleDateString(browserLocale(), { year: "numeric", month: "long" })}</Typo>
													);
												}
											} else if (key === "payment_count" && value != null) {
												content = <Typo variant="medium">{Number(value).toLocaleString()}</Typo>;
											}

											return (
												<Box
													key={index + "_" + key}
													sx={{
														padding: "10px 18px",
														display: "flex",
														alignItems: "center",
														whiteSpace: "nowrap",
														borderBottom: index < items.length - 1 ? "1px solid #F1F5F9" : "none",
													}}>
													{content}
												</Box>
											);
										}),
									)}
								</Box>
							);
						})()}
						</>
					) : (
						<DectaSessionsTable items={responseItems} busy={busy} error={response?.error} headerHeight={headerHeight ?? undefined} />
					)}
				</Box>
			)}

			{noData && (
				<>
					<Spacer size="10px" />
					<NoRecords line1={msg("ui_24999c146d")} line2={msg("ui_3623c6f205")} />
				</>
			)}

			<Box
				sx={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					flexWrap: "wrap",
					position: "sticky",
					bottom: 0,
					zIndex: 2,
					backgroundColor: "#f8fafc",
					borderBottom: "0px solid #f8fafc",
					padding: "10px 0",
				}}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} />
				<Spacer grow />
				<HBox gap="10px">
					<Button disabled={busy || downloadBusy || responseItems.length === 0} onClick={() => handleDownloadCsv()} title={downloadBusy ? "..." : isMobile ? "CSV" : msg("downloadCsv")} variant="secondarySmall" />
					<Button disabled={busy} onClick={() => handleRefresh()} title={msg("refresh")} variant="primarySmall" />
				</HBox>
			</Box>
		</VBox>
	);
};
